import { useState, useCallback, useMemo } from "react";
import { Swipeable } from "react-swipeable";
import {
  useHotelsStyles,
  useModuleBEM,
  IconNameEnum,
} from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type { LodgingMediaAsset } from "@b2bportal/lodging-api";
import {
  Dialog,
  DialogContent,
  CarouselScroller,
  Icon,
  useControlledDialog,
} from "@tenant/ui";
import clsx from "clsx";
import { ImageCarousel } from "../ImageCarousel";
import { CarouselArrow } from "../ImageCarousel/Arrow";
import defaultStyles from "./ImageGallery.module.scss";
import { useImageGallery } from "./useImageGallery";
import { BackButton, ActionButton } from "@/components";

export interface ImageGalleryComponentProps extends PlatformComponentProps {
  media: LodgingMediaAsset[];
  name?: string;
  onClickChooseRooms: () => void;
  externalDialogControl?: ReturnType<typeof useControlledDialog>;
  hideExternalTriggers?: boolean;
  hideTitle?: boolean;
  hideChooseRoomButton?: boolean;
}

interface ImageCountButtonProps {
  onClick: () => void;
  className: string;
  imageCount: number;
}

const ImageCountButton = ({
  onClick,
  className,
  imageCount,
}: ImageCountButtonProps) => {
  return (
    <ActionButton
      className={className}
      defaultStyle="outlined"
      message={
        <>
          <Icon iconName={IconNameEnum.image} />
          <span>{imageCount}</span>
        </>
      }
      onClick={onClick}
    />
  );
};

interface ChooseRoomButtonProps {
  onClick: () => void;
  className: string;
  chooseRoomText: string;
}

const ChooseRoomButton = ({
  onClick,
  className,
  chooseRoomText,
}: ChooseRoomButtonProps) => {
  return (
    <ActionButton
      className={className}
      message={chooseRoomText}
      onClick={onClick}
    />
  );
};

const COMPONENT_KEY = CoreHotelsComponent.ImageGallery;

export const ImageGallery = (props: ImageGalleryComponentProps) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const {
    className,
    children,
    onClickChooseRooms,
    externalDialogControl,
    hideExternalTriggers = false,
    hideTitle = false,
    hideChooseRoomButton = false,
  } = props;
  const { context, text } = useImageGallery(props);

  // Always use two dialogs: grid view first, then carousel view
  const internalGridDialog = useControlledDialog();
  const gridDialog = externalDialogControl ?? internalGridDialog;
  const carouselDialog = useControlledDialog();

  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevSlide = useCallback(() => {
    setCurrentIndex((prev) =>
      prev === 0 ? context.images.length - 1 : prev - 1
    );
  }, [context.images.length]);

  const goToNextSlide = useCallback(() => {
    setCurrentIndex((prev) =>
      prev === context.images.length - 1 ? 0 : prev + 1
    );
  }, [context.images.length]);

  const imageUrls = useMemo(
    () => context.images.map((image) => image.url),
    [context.images]
  );

  const handleOpenGrid = useCallback(
    (index: number = 0) => {
      setCurrentIndex(index);
      gridDialog.openDialog();
    },
    [gridDialog]
  );

  const handleGridImageClick = useCallback(
    (index: number) => {
      setCurrentIndex(index);
      gridDialog.closeDialog();
      carouselDialog.openDialog();
    },
    [gridDialog, carouselDialog]
  );

  const handleBackFromCarousel = useCallback(() => {
    carouselDialog.closeDialog();
    gridDialog.openDialog();
  }, [gridDialog, carouselDialog]);

  const handleChooseRoomFromGrid = useCallback(() => {
    onClickChooseRooms();
    gridDialog.closeDialog();
  }, [gridDialog, onClickChooseRooms]);

  const handleChooseRoomFromCarousel = useCallback(() => {
    onClickChooseRooms();
    carouselDialog.closeDialog();
    gridDialog.closeDialog();
  }, [carouselDialog, gridDialog, onClickChooseRooms]);

  return (
    <div className={clsx(block, className)}>
      {!hideExternalTriggers && (
        <>
          {/* Mobile Carousel */}
          <div className={cn("mobile-carousel")}>
            <ImageCarousel
              imageUrlsArray={imageUrls}
              onClick={() => handleOpenGrid(0)}
              hideArrows={false}
              hideDots={false}
            />
            <ImageCountButton
              className={cn("mobile-image-count-button")}
              imageCount={context.images.length}
              onClick={() => handleOpenGrid(0)}
            />
          </div>

          {/* Desktop Grid */}
          <div
            className={cn("showcase", {
              [context.imageColumns ?? ""]: context.imageColumns != null,
            })}
          >
            {context.showCaseImages.map((image, index) => (
              <button
                key={index}
                onClick={() => handleOpenGrid(index)}
                className={cn("showcase-image", [`image-${index}`])}
              >
                <img
                  className={cn("image")}
                  alt={text.getAltText(image)}
                  src={image.url}
                />
              </button>
            ))}
            <ImageCountButton
              className={cn("image-count-button")}
              imageCount={context.images.length}
              onClick={() => handleOpenGrid(0)}
            />
          </div>
        </>
      )}
      {/* Grid Dialog - shown first when using external control */}
      <Dialog {...gridDialog.props}>
        <DialogContent
          className={cn("dialog", ["grid-dialog"])}
          fullScreen={true}
        >
          <div className={cn("grid-dialog-container")}>
            <div className={cn("grid-dialog-header")}>
              <BackButton
                as="button"
                className={cn("grid-back-button")}
                onClick={gridDialog.closeDialog}
              />
              {!hideTitle && <h2 className={cn("grid-title")}>{props.name}</h2>}
              {!hideChooseRoomButton && (
                <div className={cn("choose-room-button-container")}>
                  <ChooseRoomButton
                    className={cn("choose-room-button")}
                    chooseRoomText={text.chooseRoom}
                    onClick={handleChooseRoomFromGrid}
                  />
                </div>
              )}
            </div>
            <div className={cn("grid-content")}>
              <div className={cn("image-grid")}>
                {context.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => handleGridImageClick(index)}
                    className={cn("grid-image-button")}
                  >
                    <img
                      className={cn("grid-image")}
                      alt={text.getAltText(image)}
                      src={image.url}
                    />
                  </button>
                ))}
              </div>
              {!hideChooseRoomButton && (
                <ChooseRoomButton
                  className={cn("choose-room-button-sticky")}
                  chooseRoomText={text.chooseRoom}
                  onClick={handleChooseRoomFromGrid}
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Carousel Dialog - shown when clicking image in grid */}
      <Dialog {...carouselDialog.props}>
        <DialogContent fullScreen={true} className={cn("dialog")}>
          <div className={cn("dialog-wrapper")}>
            <div className={cn("dialog-header")}>
              <BackButton
                as="button"
                className={cn("back-button")}
                onClick={handleBackFromCarousel}
              />
              <h2 className={cn("image-counter")} aria-live="polite">
                {[currentIndex + 1, context.images.length].join(" / ")}
              </h2>
              {!hideChooseRoomButton && (
                <div className={cn("choose-room-button-container")}>
                  <ChooseRoomButton
                    className={cn("choose-room-button")}
                    chooseRoomText={text.chooseRoom}
                    onClick={handleChooseRoomFromCarousel}
                  />
                </div>
              )}
            </div>
            <div className={cn("content-container")}>
              <Swipeable
                className={cn("carousel-container")}
                preventDefaultTouchmoveEvent
                onSwipedLeft={goToNextSlide}
                onSwipedRight={goToPrevSlide}
              >
                <div className={cn("main-image-wrapper")}>
                  <div className={cn("image-container")}>
                    <img
                      className={cn("main-image")}
                      alt={text.getAltText(context.images[currentIndex])}
                      src={context.images[currentIndex]?.url}
                    />
                  </div>
                  {context.images.length > 1 && (
                    <div className={cn("carousel-arrows")}>
                      <CarouselArrow
                        direction="left"
                        onClickEvent={goToPrevSlide}
                        className={cn("carousel-arrow")}
                      />
                      <CarouselArrow
                        direction="right"
                        onClickEvent={goToNextSlide}
                        className={cn("carousel-arrow")}
                      />
                    </div>
                  )}
                </div>
              </Swipeable>
              {context.images.length > 1 && (
                <div className={cn("thumbnails-wrapper")}>
                  <CarouselScroller
                    className={cn("thumbnails-scroller")}
                    buttonClassName={cn("thumbnail-scroller-arrow")}
                    contentClassName={cn("thumbnails-content")}
                  >
                    {context.images.map((image, index) => (
                      <button
                        key={index}
                        className={cn("thumbnail", {
                          active: index === currentIndex,
                        })}
                        onClick={() => setCurrentIndex(index)}
                      >
                        <img
                          className={cn("thumbnail-image")}
                          alt={text.getAltText(image)}
                          src={image.url}
                        />
                      </button>
                    ))}
                  </CarouselScroller>
                </div>
              )}
              {!hideChooseRoomButton && (
                <div className={cn("mobile-bottom-content")}>
                  <ChooseRoomButton
                    className={cn("mobile-choose-room-button")}
                    chooseRoomText={text.chooseRoom}
                    onClick={handleChooseRoomFromCarousel}
                  />
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {children}
    </div>
  );
};
